import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';

const SESSION_KEY = 'pubsilo_verify_params';

export default function VerifyPurchase() {
  const navigate = useNavigate();

  // ── URL params ───────────────────────────────────────────────────
  const params   = new URLSearchParams(window.location.search);
  const articleId = params.get('article');
  const returnTo  = params.get('return_to');  // already decoded by browser

  // Persist so login redirect doesn't lose them
  useEffect(() => {
    if (articleId && returnTo) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ articleId, returnTo }));
    }
  }, [articleId, returnTo]);

  // Recover if we came back from login with no query params
  const saved       = (() => { try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; } })();
  const effectiveId = articleId || saved?.articleId;
  const effectiveRt = returnTo  || saved?.returnTo;

  // ── Auth ──────────────────────────────────────────────────────────
  const { accessToken: token } = useAuth();
  const userType = localStorage.getItem('userType');
  const isLoggedIn = !!token && userType === 'user';

  // ── State ─────────────────────────────────────────────────────────
  const [article, setArticle]   = useState(null);
  const [user, setUser]         = useState(null);
  const [phase, setPhase]       = useState('loading'); // loading | auth | confirm | promo | buying | done | error
  const [msg, setMsg]           = useState('');

  // ── Load article + user ───────────────────────────────────────────
  useEffect(() => {
    if (!effectiveId) { setPhase('error'); setMsg('No article specified.'); return; }
    if (!isLoggedIn)  { setPhase('auth'); return; }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`/api/articles/embed/${effectiveId}`),
      axios.get('/api/users/me', { headers }),
    ])
      .then(([artRes, userRes]) => {
        const art  = artRes.data;
        const usr  = userRes.data;
        setArticle(art);
        setUser(usr);

        // Already purchased → generate token + redirect immediately
        const alreadyOwned = (usr.purchased || []).some(
          p => p.articleId?.toString() === String(art._id)
        );
        if (alreadyOwned) {
          fetchTokenAndRedirect(art._id);
          return;
        }

        const balance  = usr.tokens || 0;
        const cost     = art.token_cost;
        const shortfall = cost - balance;
        const needsPromo = balance > 0 && shortfall > 0 && shortfall <= 15 && (usr.promoTokens || 0) === 0;

        if (balance >= cost || needsPromo) {
          setPhase(needsPromo ? 'promo' : 'confirm');
        } else {
          setPhase('buying');
        }
      })
      .catch(err => {
        setPhase('error');
        setMsg(err.response?.data?.error || 'Failed to load article info.');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // ── Helpers ───────────────────────────────────────────────────────
  async function fetchTokenAndRedirect(artId) {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`/api/articles/embed/${artId}/token`, { headers });
      redirect(String(artId), res.data.token);
    } catch {
      setPhase('error');
      setMsg('Could not generate access token. Please contact support.');
    }
  }

  function redirect(artId, embedToken) {
    sessionStorage.removeItem(SESSION_KEY);
    const sep = effectiveRt.includes('?') ? '&' : '?';
    window.location.href = `${effectiveRt}${sep}article=${encodeURIComponent(artId)}&token=${encodeURIComponent(embedToken)}`;
  }

  async function handlePurchase() {
    setPhase('loading');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`/api/users/me/purchase/${article._id}`, {}, { headers });
      await fetchTokenAndRedirect(article._id);
    } catch (err) {
      setPhase('error');
      setMsg(err.response?.data?.error || 'Purchase failed. Please try again.');
    }
  }

  // ── Links preserving return state ────────────────────────────────
  const verifyParams = new URLSearchParams({ article: effectiveId || '', return_to: effectiveRt || '' });
  const loginHref        = `/login?next=${encodeURIComponent(`/verify?${verifyParams}`)}`;
  const signupHref       = `/signup/reader?next=${encodeURIComponent(`/verify?${verifyParams}`)}`;
  const tokenPurchaseHref = `/tokens/purchase?next=${encodeURIComponent(`/verify?${verifyParams}`)}`;

  // ── UI ────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh', backgroundColor: 'var(--pubsilo-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '2rem 1rem',
    }}>
      <div style={{
        background: '#fff', borderRadius: '14px', padding: '2.5rem 2rem',
        maxWidth: '440px', width: '100%',
        boxShadow: '0 12px 40px rgba(13,49,101,0.15)',
      }}>
        {/* Logo */}
        <img src="/pubsilo.png" alt="PubSilo" style={{ height: 56, objectFit: 'contain', display: 'block', margin: '0 auto 1.5rem' }} />

        {/* ── LOADING ── */}
        {phase === 'loading' && (
          <p style={{ textAlign: 'center', color: 'var(--pubsilo-muted)' }}>Loading…</p>
        )}

        {/* ── ERROR ── */}
        {phase === 'error' && (
          <>
            <h2 style={hd}>Something went wrong</h2>
            <p style={sub}>{msg}</p>
            <a href={effectiveRt || '/'} style={btnPrimary}>← Return to article</a>
          </>
        )}

        {/* ── NOT LOGGED IN ── */}
        {phase === 'auth' && (
          <>
            <h2 style={hd}>Access this article on PubSilo</h2>
            <p style={sub}>Sign in or create a free account to purchase and unlock content.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
              <a href={loginHref} style={btnPrimary}>Sign In</a>
              <a href={signupHref} style={btnSecondary}>Create Account</a>
            </div>
            {effectiveRt && (
              <a href={effectiveRt} style={backLink}>← Return without purchasing</a>
            )}
          </>
        )}

        {/* ── CONFIRM PURCHASE ── */}
        {(phase === 'confirm' || phase === 'promo') && article && user && (
          <>
            <div style={{ marginBottom: '1.5rem' }}>
              {article.genre_tree?.main && (
                <div style={{ fontSize: '0.72rem', color: 'var(--pubsilo-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>
                  {article.genre_tree.main}{article.genre_tree.sub ? ` / ${article.genre_tree.sub}` : ''}
                </div>
              )}
              <h2 style={{ ...hd, marginBottom: '0.35rem' }}>{article.title}</h2>
              {article.company_name && <p style={{ ...sub, marginBottom: 0 }}>Published by <strong>{article.company_name}</strong></p>}
              {article.writer_name?.[0] && <p style={sub}>Written by <strong>{article.writer_name[0]}</strong></p>}
            </div>

            {/* Token info */}
            <div style={{
              background: 'var(--pubsilo-light)', borderRadius: '9px',
              padding: '1rem 1.25rem', marginBottom: '1.5rem',
              border: '1px solid var(--pubsilo-border)',
            }}>
              <Row label="Article price" value={`${article.token_cost} credit${article.token_cost !== 1 ? 's' : ''}`} />
              <Row label="Your balance" value={`${user.tokens} credit${user.tokens !== 1 ? 's' : ''}`} />
              {phase === 'confirm' && (
                <Row label="Balance after purchase" value={`${user.tokens - article.token_cost} credits`} highlight />
              )}
              {phase === 'promo' && (
                <Row label="Promo credits gifted" value={`${article.token_cost - user.tokens} credit${(article.token_cost - user.tokens) !== 1 ? 's' : ''}`} highlight />
              )}
              {phase === 'promo' && (
                <Row label="Balance after purchase" value="0 credits" highlight warn />
              )}
            </div>

            {phase === 'promo' && (
              <p style={{ fontSize: '0.85rem', color: '#1a5e9a', marginBottom: '1rem', background: '#eef4ff', borderRadius: '7px', padding: '0.75rem 1rem', border: '1px solid #b3d0f5' }}>
                You're {article.token_cost - user.tokens} credit{article.token_cost - user.tokens !== 1 ? 's' : ''} short. We'll gift you the difference so you can purchase now.
              </p>
            )}

            <button onClick={() => handlePurchase()} style={btnPrimaryBtn}>
              {phase === 'promo'
                ? `Purchase · ${article.token_cost} tokens (${article.token_cost - user.tokens} gifted)`
                : `Purchase · ${article.token_cost} tokens`}
            </button>

            {phase === 'promo' && (
              <button onClick={() => navigate(tokenPurchaseHref)} style={{ ...btnSecondaryBtn, marginTop: '0.5rem' }}>
                Buy More Tokens Instead
              </button>
            )}

            {effectiveRt && (
              <a href={effectiveRt} style={backLink}>← Return without purchasing</a>
            )}
          </>
        )}

        {/* ── NOT ENOUGH TOKENS ── */}
        {phase === 'buying' && article && user && (
          <>
            <h2 style={hd}>Not enough Article Credits</h2>
            <div style={{
              background: 'var(--pubsilo-light)', borderRadius: '9px',
              padding: '1rem 1.25rem', marginBottom: '1.5rem',
              border: '1px solid var(--pubsilo-border)',
            }}>
              <Row label="Article price" value={`${article.token_cost} credits`} />
              <Row label="Your balance" value={`${user.tokens} credits`} />
              <Row label="You need" value={`${article.token_cost - user.tokens} more credits`} highlight warn />
            </div>
            <p style={{ fontSize: '0.88rem', color: 'var(--pubsilo-muted)', marginBottom: '1.25rem' }}>
              Purchase Article Credits to unlock <strong>{article.title}</strong>.
            </p>
            <button
              onClick={() => navigate(tokenPurchaseHref)}
              style={btnPrimaryBtn}
            >
              Buy Article Credits
            </button>
            {effectiveRt && (
              <a href={effectiveRt} style={backLink}>← Return without purchasing</a>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Small helpers ──────────────────────────────────────────────────
function Row({ label, value, highlight, warn }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', padding: '0.2rem 0' }}>
      <span style={{ color: 'var(--pubsilo-muted)' }}>{label}</span>
      <span style={{ fontWeight: 700, color: warn ? '#b06000' : highlight ? 'var(--pubsilo-blue)' : 'var(--pubsilo-text)' }}>{value}</span>
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────
const hd = { fontSize: '1.3rem', fontWeight: 800, color: 'var(--pubsilo-blue-dark)', marginBottom: '0.5rem', textAlign: 'center' };
const sub = { fontSize: '0.9rem', color: 'var(--pubsilo-muted)', textAlign: 'center', marginBottom: '0.5rem' };
const btnPrimary = {
  display: 'block', width: '100%', padding: '0.75rem', textAlign: 'center',
  backgroundColor: 'var(--pubsilo-blue)', color: '#fff', borderRadius: '7px',
  fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem',
};
const btnSecondary = {
  display: 'block', width: '100%', padding: '0.75rem', textAlign: 'center',
  backgroundColor: 'transparent', color: 'var(--pubsilo-blue)', borderRadius: '7px',
  fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem',
  border: '1.5px solid var(--pubsilo-blue)',
};
const btnPrimaryBtn = {
  ...btnPrimary, border: 'none', cursor: 'pointer', display: 'block', width: '100%',
};
const btnSecondaryBtn = {
  ...btnSecondary, cursor: 'pointer', background: 'transparent',
};
const backLink = {
  display: 'block', textAlign: 'center', marginTop: '1.25rem',
  fontSize: '0.82rem', color: 'var(--pubsilo-muted)', textDecoration: 'none',
};
